import React, { forwardRef, useRef, useMemo, useEffect, useState, useCallback } from "react";
import { AnimatePresence, motion, useAnimation } from "framer-motion";
import { FloatingPortal, FloatingFocusManager } from "@floating-ui/react";
import { useLightboxContext } from "../hooks/useLightboxContext";
import { useInlineExpansion } from "../hooks/useInlineExpansion";
import { InlineContainerContext } from "../context";
import { icons, colors2 } from "oolib";

const { ArrowLeft } = icons;
import {
    StyledInlinePanel,
    StyledInlineRootPanelContent,
    StyledPanelOverlay,
    StyledBackButton,
    StyledLightboxTitle,
    LIGHTBOX_PANEL_WIDTH,
    LIGHTBOX_PANEL_MAX_OFFSET,
    LIGHTBOX_PANEL_MIN_OFFSET,
    LIGHTBOX_VIEWPORT_PADDING,
    INLINE_PANEL_TRANSITION,
} from "../styled";
import type { LightboxContentProps, InlineContainerContextValue } from "../types";

const MAX_OFFSET_REM = LIGHTBOX_PANEL_WIDTH * LIGHTBOX_PANEL_MAX_OFFSET;
const MIN_OFFSET_REM = LIGHTBOX_PANEL_WIDTH * LIGHTBOX_PANEL_MIN_OFFSET;

/**
 * Inline mode root renderer.
 * The only floating element in inline mode — positions via floating-ui.
 * Creates a flex container that child panels portal into.
 * Animates width as child panels open/close.
 */
export const InlineRootContent = forwardRef<HTMLDivElement, LightboxContentProps>(
    function InlineRootContent(
        {
            children,
            title,
            className,
            style,
            trapFocus = true,
            initialFocus = 0,
            onEscapeKeyDown,
        },
        forwardedRef
    ) {
        const {
            refs,
            floatingStyles,
            getFloatingProps,
            context,
            isMounted,
            contentId,
        } = useLightboxContext();

        const containerRef = useRef<HTMLDivElement>(null);
        const rootPanelRef = useRef<HTMLDivElement>(null);
        const panelOffsetRef = useRef<number>(MAX_OFFSET_REM);
        const [contentHeight, setContentHeight] = useState<number | undefined>(undefined);
        const roRef = useRef<ResizeObserver | null>(null);
        const controls = useAnimation();

        /** Compute the ideal panel offset (in rem) so all panels fit within the viewport */
        const computeOffset = useCallback((count: number): number => {
            if (count === 0) return MAX_OFFSET_REM;
            const remPx = parseFloat(getComputedStyle(document.documentElement).fontSize);
            const left = parseFloat(floatingStyles.left as string) || 0;
            const available = window.innerWidth - LIGHTBOX_VIEWPORT_PADDING;
            const maxWidthRem = (available - left) / remPx;
            const requiredOffset = (maxWidthRem - LIGHTBOX_PANEL_WIDTH) / count;
            return Math.max(MIN_OFFSET_REM, Math.min(MAX_OFFSET_REM, requiredOffset));
        }, [floatingStyles.left]);

        // Imperative callback: root container animates width shrink on exit
        const onContainerExitStart = useCallback((fromDepth: number, remainingCount: number) => {
            const offset = computeOffset(remainingCount);
            panelOffsetRef.current = offset;
            controls.start({
                width: `${LIGHTBOX_PANEL_WIDTH + remainingCount * offset}rem`,
            });
            // Set data-children-exiting imperatively on root panel
            if (rootPanelRef.current && fromDepth <= 1) {
                rootPanelRef.current.setAttribute('data-children-exiting', 'true');
            }
        }, [controls, computeOffset]);

        const {
            panelCount,
            maxVisibleDepth,
            registerPanel,
            unregisterPanel,
            isPanelRegistered,
            triggerClose,
            commitClose,
            exitingFromDepthRef,
            resetAll,
        } = useInlineExpansion(onContainerExitStart);

        // Callback ref — measures the full content area (root + child panels)
        // via ResizeObserver. The outer container animates to this value so
        // it always fits the tallest visible panel.
        // Skips updates during exit to avoid a secondary pre-paint re-render.
        const contentMeasureRef = useCallback((el: HTMLDivElement | null) => {
            if (roRef.current) {
                roRef.current.disconnect();
                roRef.current = null;
            }
            if (!el) return;
            const ro = new ResizeObserver(() => {
                if (exitingFromDepthRef.current !== null) return;
                const h = el.offsetHeight;
                if (h > 0) setContentHeight(h);
            });
            ro.observe(el);
            roRef.current = ro;
        }, [exitingFromDepthRef]);

        // When the container is dismissed (click-outside, etc.), clear the
        // panel registry synchronously so no stale entries survive into a
        // potential reopen.  Child cleanup effects are async and may not
        // have fired yet.
        useEffect(() => {
            if (!isMounted) {
                resetAll();
                setContentHeight(undefined);
            }
        }, [isMounted, resetAll]);

        // Compute offset during render so children see the correct value immediately
        const currentPanelOffset = useMemo(() => {
            if (!isMounted) return MAX_OFFSET_REM;
            return computeOffset(panelCount);
        }, [isMounted, panelCount, computeOffset]);
        panelOffsetRef.current = currentPanelOffset;

        // Drive container width/height via imperative controls on normal open/resize
        useEffect(() => {
            if (!isMounted) return;
            controls.start({
                width: `${LIGHTBOX_PANEL_WIDTH + panelCount * panelOffsetRef.current}rem`,
                opacity: 1,
                height: contentHeight ?? "auto",
            });
        }, [panelCount, contentHeight, isMounted, controls, currentPanelOffset]);

        // Recompute offset on window resize
        useEffect(() => {
            if (!isMounted) return;
            const handleResize = () => {
                const offset = computeOffset(panelCount);
                panelOffsetRef.current = offset;
                controls.start({
                    width: `${LIGHTBOX_PANEL_WIDTH + panelCount * offset}rem`,
                });
            };
            window.addEventListener('resize', handleResize);
            return () => window.removeEventListener('resize', handleResize);
        }, [isMounted, panelCount, controls, computeOffset]);

        // Clear data-children-exiting after commitClose render
        // (panelCount changes after commitClose, exitingFromDepthRef is null)
        useEffect(() => {
            if (exitingFromDepthRef.current === null && rootPanelRef.current) {
                rootPanelRef.current.removeAttribute('data-children-exiting');
            }
        }, [panelCount, exitingFromDepthRef]);

        // Delay overlay/back button appearance by 300ms after a child panel becomes visible
        const hasChildPanel = maxVisibleDepth >= 1;
        const [showOverlay, setShowOverlay] = useState(false);
        useEffect(() => {
            if (hasChildPanel) {
                const timer = setTimeout(() => setShowOverlay(true), 300);
                return () => clearTimeout(timer);
            }
            setShowOverlay(false);
        }, [hasChildPanel]);

        const inlineCtxValue = useMemo<InlineContainerContextValue>(
            () => ({
                containerRef,
                panelOffsetRef,
                registerPanel,
                unregisterPanel,
                isPanelRegistered,
                triggerClose,
                commitClose,
                exitingFromDepthRef,
                panelCount,
                maxVisibleDepth,
            }),
            [registerPanel, unregisterPanel, isPanelRegistered, triggerClose, commitClose, exitingFromDepthRef, panelCount, maxVisibleDepth]
        );

        const setRef = (node: HTMLDivElement | null) => {
            refs.setFloating(node);
            if (typeof forwardedRef === "function") {
                forwardedRef(node);
            } else if (forwardedRef) {
                forwardedRef.current = node;
            }
        };

        // Separate zIndex from panel style — it belongs on the floating container
        const { zIndex, ...panelStyle } = style || {} as any;

        const content = (
            <StyledInlinePanel
                ref={setRef}
                id={contentId}
                className={className}
                {...getFloatingProps({
                    onKeyDown: (e: React.KeyboardEvent) => {
                        if (e.key === "Escape" && onEscapeKeyDown) {
                            onEscapeKeyDown(e.nativeEvent);
                        }
                    },
                })}
                initial={{ width: 0, opacity: 0 }}
                animate={controls}
                exit={{ width: 0, opacity: 0 }}
                transition={{
                    ...INLINE_PANEL_TRANSITION,
                    height: { type: "tween", duration: 0.15, ease: "easeOut" },
                }}
                style={{ ...floatingStyles, ...(zIndex != null && { zIndex }) }}
            >
                {/* Inner flex wrapper — ResizeObserver measures this to get
                    the natural height of the tallest visible panel */}
                <div ref={contentMeasureRef} style={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch', alignSelf: 'flex-start', maxHeight: '80vh' }}>
                    {/* Root panel — always visible when container is mounted */}
                    <StyledInlineRootPanelContent
                        ref={rootPanelRef}
                        $cascade
                        style={{ ...panelStyle, width: `${LIGHTBOX_PANEL_WIDTH}rem`, '--panel-offset': `${currentPanelOffset}rem`, position: 'relative', zIndex: 1, flexShrink: 0, alignSelf: 'stretch' } as any}
                    >
                        {title && <StyledLightboxTitle>{title}</StyledLightboxTitle>}
                        <InlineContainerContext.Provider value={inlineCtxValue}>
                            {children}
                        </InlineContainerContext.Provider>
                        {showOverlay && (
                            <div
                                style={{ position: 'absolute', inset: 0, zIndex: 10, pointerEvents: 'none' }}
                            >
                                <StyledPanelOverlay onClick={() => triggerClose(1)} />
                                <StyledBackButton onClick={() => triggerClose(1)}>
                                    <ArrowLeft size={20} color={colors2.grey80} weight="bold" />
                                </StyledBackButton>
                            </div>
                        )}
                    </StyledInlineRootPanelContent>

                    {/* Child panels portal into this element via createPortal */}
                    <div ref={containerRef} style={{ display: 'contents' }} />
                </div>
            </StyledInlinePanel>
        );

        const wrappedContent = trapFocus ? (
            <FloatingFocusManager
                context={context}
                modal={false}
                initialFocus={initialFocus}
                returnFocus
            >
                {content}
            </FloatingFocusManager>
        ) : (
            content
        );

        return (
            <FloatingPortal>
                <AnimatePresence>
                    {isMounted && wrappedContent}
                </AnimatePresence>
            </FloatingPortal>
        );
    }
);
